import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useData } from "../../context/DataContext";
import {
  createCompanyPolicy,
  deleteCompanyPolicy,
  getCompanyPolicies,
  publishCompanyPolicy,
  unpublishCompanyPolicy,
  updateCompanyPolicy,
} from "../../api/companyPolicies";
import { uploadAttachments } from "../../api/attachments";
import AttachmentList from "../common/AttachmentList";
import AttachmentUploader from "../common/AttachmentUploader";
import "./HrCompanyPolicies.css";

const TARGET_OPTIONS = [
  { value: "ALL", label: "كل الموظفين" },
  { value: "DEPARTMENT", label: "إدارات محددة" },
  { value: "JOB_TITLE", label: "مسميات وظيفية محددة" },
  { value: "EMPLOYEES", label: "موظفين محددين" },
];

function getApiErrorMessage(err, fallback) {
  const message = err?.response?.data?.message;
  if (Array.isArray(message)) return message.join("\n");
  if (typeof message === "string" && message.trim()) return message;
  return err?.message || fallback;
}

function statusLabel(status) {
  if (status === "DRAFT") return "مسودة";
  if (status === "PUBLISHED") return "منشور";
  return status || "—";
}

function targetLabel(item) {
  const target = item?.target || "ALL";
  if (target === "ALL") return "كل الموظفين";
  if (target === "DEPARTMENT") {
    const names = (item.departments || [])
      .map((d) => d.name)
      .filter(Boolean);
    if (names.length) return `إدارات: ${names.join("، ")}`;
    const count = item.departmentCount ?? 0;
    return `إدارات محددة (${count})`;
  }
  if (target === "JOB_TITLE") {
    const titles = (item.jobTitles || []).filter(Boolean);
    if (titles.length) return `مسميات: ${titles.join("، ")}`;
    const count = item.jobTitleCount ?? 0;
    return `مسميات وظيفية (${count})`;
  }
  if (target === "EMPLOYEES") {
    const count = item.recipientCount ?? item.recipients?.length ?? 0;
    return `موظفين محددين (${count})`;
  }
  return target;
}

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("ar-EG", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return String(value);
  }
}

function publishSuccessMessage(target) {
  if (target === "DEPARTMENT") return "تم نشر البند للإدارات المحددة";
  if (target === "JOB_TITLE") return "تم نشر البند للمسميات الوظيفية المحددة";
  if (target === "EMPLOYEES") return "تم نشر البند للموظفين المحددين";
  return "تم نشر البند لكل الموظفين";
}

const EMPTY_FORM = {
  title: "",
  summary: "",
  body: "",
  publishNow: true,
};

function HrCompanyPolicies() {
  const { employeesByBranch, departments } = useData();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [actionId, setActionId] = useState("");
  const [editingId, setEditingId] = useState("");
  const [title, setTitle] = useState(EMPTY_FORM.title);
  const [summary, setSummary] = useState(EMPTY_FORM.summary);
  const [body, setBody] = useState(EMPTY_FORM.body);
  const [target, setTarget] = useState("ALL");
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState([]);
  const [selectedJobTitles, setSelectedJobTitles] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [empSearch, setEmpSearch] = useState("");
  const [deptSearch, setDeptSearch] = useState("");
  const [jobTitleSearch, setJobTitleSearch] = useState("");
  const [publishNow, setPublishNow] = useState(EMPTY_FORM.publishNow);
  const [notifyOnUpdate, setNotifyOnUpdate] = useState(false);
  const [listFilter, setListFilter] = useState("all");
  const [files, setFiles] = useState([]);
  const [attachmentsRefresh, setAttachmentsRefresh] = useState(0);

  const allEmployees = useMemo(
    () =>
      Object.values(employeesByBranch || {})
        .flat()
        .filter((e) => e.status !== "terminated"),
    [employeesByBranch],
  );

  const activeDepartments = useMemo(
    () =>
      (departments || []).filter(
        (d) => d.isActive !== false && (d.backendId || d.id),
      ),
    [departments],
  );

  const filteredDepartments = useMemo(() => {
    const q = deptSearch.trim().toLowerCase();
    if (!q) return activeDepartments;
    return activeDepartments.filter(
      (d) =>
        d.name?.toLowerCase().includes(q) ||
        d.code?.toLowerCase().includes(q),
    );
  }, [activeDepartments, deptSearch]);

  const jobTitleOptions = useMemo(() => {
    const counts = new Map();
    for (const emp of allEmployees) {
      const title = String(emp.jobTitle || "").trim();
      if (!title) continue;
      counts.set(title, (counts.get(title) || 0) + 1);
    }
    return [...counts.entries()]
      .sort((a, b) => a[0].localeCompare(b[0], "ar"))
      .map(([title, count]) => ({ title, count }));
  }, [allEmployees]);

  const filteredJobTitles = useMemo(() => {
    const q = jobTitleSearch.trim().toLowerCase();
    if (!q) return jobTitleOptions;
    return jobTitleOptions.filter((row) =>
      row.title.toLowerCase().includes(q),
    );
  }, [jobTitleOptions, jobTitleSearch]);

  const filteredEmployees = useMemo(() => {
    const q = empSearch.trim().toLowerCase();
    if (!q) return allEmployees;
    return allEmployees.filter(
      (e) =>
        e.name?.toLowerCase().includes(q) ||
        e.code?.toLowerCase().includes(q) ||
        e.jobTitle?.toLowerCase().includes(q) ||
        e.departmentName?.toLowerCase().includes(q),
    );
  }, [allEmployees, empSearch]);

  const filteredItems = useMemo(() => {
    if (listFilter === "all") return items;
    return items.filter((item) => item.status === listFilter);
  }, [items, listFilter]);

  const loadItems = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getCompanyPolicies();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر تحميل لائحة الشركة"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadItems();
  }, [loadItems]);

  function resetForm() {
    setEditingId("");
    setTitle("");
    setSummary("");
    setBody("");
    setTarget("ALL");
    setSelectedDepartmentIds([]);
    setSelectedJobTitles([]);
    setSelectedIds([]);
    setEmpSearch("");
    setDeptSearch("");
    setJobTitleSearch("");
    setPublishNow(true);
    setNotifyOnUpdate(false);
    setFiles([]);
  }

  function startEdit(item) {
    setEditingId(item.id);
    setTitle(item.title || "");
    setSummary(item.summary || "");
    setBody(item.body || "");
    setTarget(item.target || "ALL");
    setSelectedDepartmentIds((item.departments || []).map((d) => d.id));
    setSelectedJobTitles(item.jobTitles || []);
    setSelectedIds(
      (item.recipients || []).map((row) => row.id || row.employeeId),
    );
    setPublishNow(item.status !== "PUBLISHED");
    setNotifyOnUpdate(false);
    setFiles([]);
    setError("");
    setSuccess("");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  function toggleDepartment(id) {
    setSelectedDepartmentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleJobTitle(title) {
    setSelectedJobTitles((prev) =>
      prev.includes(title) ? prev.filter((x) => x !== title) : [...prev, title],
    );
  }

  function toggleEmployee(id) {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function selectAllFilteredDepartments() {
    const ids = filteredDepartments.map((d) => d.backendId || d.id);
    setSelectedDepartmentIds((prev) => [...new Set([...prev, ...ids])]);
  }

  function selectAllFilteredJobTitles() {
    const titles = filteredJobTitles.map((row) => row.title);
    setSelectedJobTitles((prev) => [...new Set([...prev, ...titles])]);
  }

  function selectAllFilteredEmployees() {
    const ids = filteredEmployees.map((e) => e.id);
    setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
  }

  function buildPayload() {
    const payload = {
      title: title.trim(),
      summary: summary.trim() || undefined,
      body: body.trim(),
      target,
    };
    if (target === "DEPARTMENT") payload.departmentIds = selectedDepartmentIds;
    if (target === "JOB_TITLE") payload.jobTitles = selectedJobTitles;
    if (target === "EMPLOYEES") payload.employeeIds = selectedIds;
    return payload;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (!title.trim()) {
      setError("عنوان اللائحة مطلوب");
      return;
    }
    if (!body.trim() && !files.length && !editingId) {
      setError("اكتب نص اللائحة أو ارفع ملف اللائحة");
      return;
    }
    if (target === "DEPARTMENT" && !selectedDepartmentIds.length) {
      setError("اختر إدارة واحدة على الأقل");
      return;
    }
    if (target === "JOB_TITLE" && !selectedJobTitles.length) {
      setError("اختر مسمى وظيفي واحداً على الأقل");
      return;
    }
    if (target === "EMPLOYEES" && !selectedIds.length) {
      setError("اختر موظفاً واحداً على الأقل");
      return;
    }

    setSubmitting(true);
    setError("");
    setSuccess("");
    try {
      if (editingId) {
        const updated = await updateCompanyPolicy(editingId, {
          ...buildPayload(),
          publish: publishNow,
          notify: notifyOnUpdate,
        });
        if (files.length) {
          await uploadAttachments("company_policy", updated.id, files);
        }
        setSuccess("تم تحديث بند اللائحة");
      } else {
        const created = await createCompanyPolicy({
          ...buildPayload(),
          publish: publishNow,
        });
        if (files.length) {
          await uploadAttachments("company_policy", created.id, files);
        }
        setSuccess(
          publishNow
            ? publishSuccessMessage(target)
            : "تم حفظ البند كمسودة",
        );
      }
      resetForm();
      setAttachmentsRefresh((n) => n + 1);
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حفظ اللائحة"));
    } finally {
      setSubmitting(false);
    }
  }

  async function handlePublish(id) {
    const item = items.find((row) => row.id === id);
    setActionId(id);
    setError("");
    setSuccess("");
    try {
      await publishCompanyPolicy(id);
      setSuccess(publishSuccessMessage(item?.target || "ALL"));
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر نشر اللائحة"));
    } finally {
      setActionId("");
    }
  }

  async function handleUnpublish(id) {
    setActionId(id);
    setError("");
    setSuccess("");
    try {
      await unpublishCompanyPolicy(id);
      setSuccess("تم إخفاء البند عن الموظفين");
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر إخفاء اللائحة"));
    } finally {
      setActionId("");
    }
  }

  async function handleDelete(id) {
    if (!window.confirm("حذف هذا البند من لائحة الشركة؟")) return;
    setActionId(id);
    setError("");
    setSuccess("");
    try {
      await deleteCompanyPolicy(id);
      if (editingId === id) resetForm();
      setSuccess("تم حذف البند");
      await loadItems();
    } catch (err) {
      setError(getApiErrorMessage(err, "تعذر حذف اللائحة"));
    } finally {
      setActionId("");
    }
  }

  return (
    <div className="hr-company-policies" dir="rtl">
      <div className="hr-company-policies-header">
        <div>
          <h1 className="hr-company-policies-title">إدارة لائحة الشركة</h1>
          <p className="hr-company-policies-subtitle">
            ارفع لائحة لكل الشركة أو لإدارات محددة أو لمسميات وظيفية أو لموظفين
            معيّنين، ثم انشرها لتظهر للمستهدفين فقط.
          </p>
        </div>
      </div>

      {error ? (
        <div className="hr-company-policies-alert error">{error}</div>
      ) : null}
      {success ? (
        <div className="hr-company-policies-alert success">{success}</div>
      ) : null}

      <form className="hr-company-policies-form" onSubmit={handleSubmit}>
        <h2 className="hr-company-policies-section-title">
          {editingId ? "تعديل بند اللائحة" : "بند جديد في اللائحة"}
        </h2>

        <label className="hr-company-policies-field">
          العنوان
          <input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="مثال: لائحة إدارة التسويق"
            required
          />
        </label>

        <label className="hr-company-policies-field">
          ملخص قصير (اختياري)
          <input
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            maxLength={400}
            placeholder="يظهر في قائمة الموظفين قبل فتح النص الكامل"
          />
        </label>

        <label className="hr-company-policies-field">
          نص اللائحة (اختياري إذا تم رفع ملف)
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={10}
            placeholder="اكتب بنود اللائحة أو اتركه فارغاً واكتفِ برفع ملف PDF/Word"
          />
        </label>

        <div className="hr-company-policies-field">
          <span>هذه اللائحة خاصة بـ</span>
          <div className="hr-company-policies-target-row">
            {TARGET_OPTIONS.map((opt) => (
              <label key={opt.value} className="hr-company-policies-radio">
                <input
                  type="radio"
                  name="policy-target"
                  value={opt.value}
                  checked={target === opt.value}
                  onChange={() => setTarget(opt.value)}
                />
                {opt.label}
              </label>
            ))}
          </div>
        </div>

        {target === "DEPARTMENT" ? (
          <div className="hr-company-policies-picker">
            <div className="hr-company-policies-picker-toolbar">
              <input
                type="search"
                className="hr-company-policies-search"
                value={deptSearch}
                onChange={(e) => setDeptSearch(e.target.value)}
                placeholder="بحث عن إدارة..."
              />
              <div className="hr-company-policies-picker-actions">
                <button type="button" onClick={selectAllFilteredDepartments}>
                  تحديد الظاهر
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedDepartmentIds([])}
                >
                  مسح التحديد
                </button>
                <span className="hr-company-policies-selected-count">
                  {selectedDepartmentIds.length} إدارة
                </span>
              </div>
            </div>
            <div className="hr-company-policies-picker-list">
              {filteredDepartments.length === 0 ? (
                <div className="hr-company-policies-empty-inline">
                  لا توجد إدارات
                </div>
              ) : (
                filteredDepartments.map((dept) => {
                  const id = dept.backendId || dept.id;
                  return (
                    <label key={id} className="hr-company-policies-picker-row">
                      <input
                        type="checkbox"
                        checked={selectedDepartmentIds.includes(id)}
                        onChange={() => toggleDepartment(id)}
                      />
                      <span className="hr-company-policies-picker-name">
                        {dept.name}
                      </span>
                      <span className="hr-company-policies-picker-meta">
                        {dept.code || ""}
                      </span>
                    </label>
                  );
                })
              )}
            </div>
          </div>
        ) : null}

        {target === "JOB_TITLE" ? (
          <div className="hr-company-policies-picker">
            <div className="hr-company-policies-picker-toolbar">
              <input
                type="search"
                className="hr-company-policies-search"
                value={jobTitleSearch}
                onChange={(e) => setJobTitleSearch(e.target.value)}
                placeholder="بحث عن مسمى وظيفي..."
              />
              <div className="hr-company-policies-picker-actions">
                <button type="button" onClick={selectAllFilteredJobTitles}>
                  تحديد الظاهر
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedJobTitles([])}
                >
                  مسح التحديد
                </button>
                <span className="hr-company-policies-selected-count">
                  {selectedJobTitles.length} مسمى
                </span>
              </div>
            </div>
            <div className="hr-company-policies-picker-list">
              {filteredJobTitles.length === 0 ? (
                <div className="hr-company-policies-empty-inline">
                  لا توجد مسميات وظيفية
                </div>
              ) : (
                filteredJobTitles.map((row) => (
                  <label
                    key={row.title}
                    className="hr-company-policies-picker-row"
                  >
                    <input
                      type="checkbox"
                      checked={selectedJobTitles.includes(row.title)}
                      onChange={() => toggleJobTitle(row.title)}
                    />
                    <span className="hr-company-policies-picker-name">
                      {row.title}
                    </span>
                    <span className="hr-company-policies-picker-meta">
                      {row.count} موظف
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}

        {target === "EMPLOYEES" ? (
          <div className="hr-company-policies-picker">
            <div className="hr-company-policies-picker-toolbar">
              <input
                type="search"
                className="hr-company-policies-search"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
                placeholder="بحث بالاسم أو الكود أو الإدارة..."
              />
              <div className="hr-company-policies-picker-actions">
                <button type="button" onClick={selectAllFilteredEmployees}>
                  تحديد الظاهر
                </button>
                <button type="button" onClick={() => setSelectedIds([])}>
                  مسح التحديد
                </button>
                <span className="hr-company-policies-selected-count">
                  {selectedIds.length} مختار
                </span>
              </div>
            </div>
            <div className="hr-company-policies-picker-list">
              {filteredEmployees.length === 0 ? (
                <div className="hr-company-policies-empty-inline">
                  لا يوجد موظفون
                </div>
              ) : (
                filteredEmployees.map((emp) => (
                  <label key={emp.id} className="hr-company-policies-picker-row">
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(emp.id)}
                      onChange={() => toggleEmployee(emp.id)}
                    />
                    <span className="hr-company-policies-picker-name">
                      {emp.name}
                    </span>
                    <span className="hr-company-policies-picker-meta">
                      {emp.code} · {emp.departmentName || emp.jobTitle}
                    </span>
                  </label>
                ))
              )}
            </div>
          </div>
        ) : null}

        <AttachmentUploader
          entityType="company_policy"
          files={files}
          onFilesChange={setFiles}
          disabled={submitting}
          label="رفع ملف اللائحة (PDF أو Word)"
        />
        <p className="hr-company-policies-hint">
          يمكن إرفاق ملف اللائحة الرسمي، ويظهر فقط للمستهدفين مع البند المنشور.
        </p>

        {!(
          editingId &&
          items.find((item) => item.id === editingId)?.status === "PUBLISHED"
        ) ? (
          <label className="hr-company-policies-checkbox">
            <input
              type="checkbox"
              checked={publishNow}
              onChange={(e) => setPublishNow(e.target.checked)}
            />
            {editingId
              ? "نشر البند ليظهر للمستهدفين"
              : "نشر فوري وإرسال إشعار للمستهدفين"}
          </label>
        ) : (
          <label className="hr-company-policies-checkbox">
            <input
              type="checkbox"
              checked={notifyOnUpdate}
              onChange={(e) => setNotifyOnUpdate(e.target.checked)}
            />
            إرسال إشعار للمستهدفين بعد هذا التحديث
          </label>
        )}

        <div className="hr-company-policies-form-actions">
          <button
            type="submit"
            className="hr-company-policies-submit"
            disabled={submitting}
          >
            {submitting
              ? "جاري الحفظ..."
              : editingId
                ? "حفظ التعديل"
                : publishNow
                  ? "نشر البند"
                  : "حفظ كمسودة"}
          </button>
          {editingId ? (
            <button
              type="button"
              className="hr-company-policies-secondary"
              onClick={resetForm}
              disabled={submitting}
            >
              إلغاء التعديل
            </button>
          ) : null}
        </div>
      </form>

      <div className="hr-company-policies-list-section">
        <div className="hr-company-policies-list-header">
          <h2 className="hr-company-policies-section-title">بنود اللائحة</h2>
          <div className="hr-company-policies-filters">
            {[
              { value: "all", label: "الكل" },
              { value: "PUBLISHED", label: "منشور" },
              { value: "DRAFT", label: "مسودة" },
            ].map((f) => (
              <button
                key={f.value}
                type="button"
                className={`hr-company-policies-filter-btn${
                  listFilter === f.value ? " active" : ""
                }`}
                onClick={() => setListFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="hr-company-policies-empty">جاري التحميل...</div>
        ) : filteredItems.length === 0 ? (
          <div className="hr-company-policies-empty">لا توجد بنود بعد</div>
        ) : (
          <div className="hr-company-policies-list">
            {filteredItems.map((item) => (
              <article key={item.id} className="hr-company-policies-card">
                <div className="hr-company-policies-card-top">
                  <h3>{item.title}</h3>
                  <span
                    className={`hr-company-policies-badge ${
                      item.status === "PUBLISHED" ? "published" : "draft"
                    }`}
                  >
                    {statusLabel(item.status)}
                  </span>
                </div>
                {item.summary ? (
                  <p className="hr-company-policies-card-summary">
                    {item.summary}
                  </p>
                ) : null}
                {item.body ? (
                  <p className="hr-company-policies-card-body">{item.body}</p>
                ) : null}
                <AttachmentList
                  entityType="company_policy"
                  entityId={item.id}
                  hideIfEmpty
                  refreshKey={attachmentsRefresh}
                />
                {item.status === "DRAFT" ? (
                  <AttachmentUploader
                    entityType="company_policy"
                    entityId={item.id}
                    disabled={actionId === item.id}
                    label="إضافة مرفق"
                    onUploaded={() => setAttachmentsRefresh((n) => n + 1)}
                  />
                ) : null}
                <div className="hr-company-policies-card-meta">
                  <span>{targetLabel(item)}</span>
                  <span>
                    {item.status === "PUBLISHED"
                      ? `نُشر: ${formatDateTime(item.publishedAt)}`
                      : `أُنشئ: ${formatDateTime(item.createdAt)}`}
                  </span>
                </div>
                <div className="hr-company-policies-card-actions">
                  <button
                    type="button"
                    className="hr-company-policies-secondary"
                    disabled={actionId === item.id}
                    onClick={() => startEdit(item)}
                  >
                    تعديل
                  </button>
                  {item.status === "DRAFT" ? (
                    <button
                      type="button"
                      className="hr-company-policies-submit"
                      disabled={actionId === item.id}
                      onClick={() => handlePublish(item.id)}
                    >
                      {actionId === item.id ? "جاري النشر..." : "نشر الآن"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="hr-company-policies-secondary"
                      disabled={actionId === item.id}
                      onClick={() => handleUnpublish(item.id)}
                    >
                      {actionId === item.id ? "جاري الإخفاء..." : "إخفاء"}
                    </button>
                  )}
                  <button
                    type="button"
                    className="hr-company-policies-danger"
                    disabled={actionId === item.id}
                    onClick={() => handleDelete(item.id)}
                  >
                    حذف
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default HrCompanyPolicies;
